// =====================================================================================
// FILE: src/pages/PortalHub.jsx
// 
// VenueFlow Portal Hub — Central directory of all club VenueFlow portals
// - Lumen8 staff: See all clubs
// - Producers: See only their allowed clubs
// =====================================================================================

import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';

// ================= THEME (matches AppShell) =================
const THEME = {
  bg: '#0a0a0f',
  surface: '#121218',
  surfaceHover: '#1a1a24',
  border: 'rgba(255, 255, 255, 0.08)',
  borderHover: 'rgba(255, 255, 255, 0.15)',
  text: '#e4e4e7',
  textMuted: '#71717a',
  accent: '#2fd6b5',
  accentDim: 'rgba(47, 214, 181, 0.1)',
};

// ================= CLUB PORTAL REGISTRY =================
// Add new clubs here as portals roll out
const CLUB_PORTALS = [
  {
    id: 'STK',
    name: 'St Kilda',
    shortName: 'Saints',
    code: 'STK',
    url: 'https://stkilda.lumen8venueflow.com',
    primaryColor: '#ED1C24',
    secondaryColor: '#000000',
    status: 'live',
  },
  {
    id: 'HAW',
    name: 'Hawthorn',
    shortName: 'Hawks',
    code: 'HAW',
    url: 'https://hawthorn.lumen8venueflow.com',
    primaryColor: '#4D2004',
    secondaryColor: '#FBBF24',
    status: 'live',
  },
  {
    id: 'MEL',
    name: 'Melbourne',
    shortName: 'Demons',
    code: 'MEL',
    url: 'https://melbourne.lumen8venueflow.com',
    primaryColor: '#0F1B8C',
    secondaryColor: '#CC0000',
    status: 'live',
  },
  {
    id: 'CAR',
    name: 'Carlton',
    shortName: 'Blues',
    code: 'CAR',
    url: 'https://carlton.lumen8venueflow.com',
    primaryColor: '#0054A4',
    secondaryColor: '#FFFFFF',
    status: 'live',
  },
  {
    id: 'ESS',
    name: 'Essendon',
    shortName: 'Bombers',
    code: 'ESS',
    url: 'https://essendon.lumen8venueflow.com',
    primaryColor: '#CC2031',
    secondaryColor: '#000000',
    status: 'live',
  },
  {
    id: 'COL',
    name: 'Collingwood',
    shortName: 'Magpies',
    code: 'COL',
    url: 'https://collingwood.lumen8venueflow.com',
    primaryColor: '#000000',
    secondaryColor: '#FFFFFF',
    status: 'live',
  },
  {
    id: 'NOR',
    name: 'North Melbourne',
    shortName: 'Kangaroos',
    code: 'NOR',
    url: 'https://northmelbourne.lumen8venueflow.com',
    primaryColor: '#013B9F',
    secondaryColor: '#FFFFFF',
    status: 'live',
  },
  {
    id: 'WBD',
    name: 'Western Bulldogs',
    shortName: 'Bulldogs',
    code: 'WBD',
    url: 'https://westernbulldogs.lumen8venueflow.com',
    primaryColor: '#0039A6',
    secondaryColor: '#FFFFFF',
    status: 'live',
  },
  {
    id: 'RIC',
    name: 'Richmond',
    shortName: 'Tigers',
    code: 'RIC',
    url: 'https://richmond.lumen8venueflow.com',
    primaryColor: '#FFA500',
    secondaryColor: '#000000',
    status: 'live',
  },
  {
    id: 'FIN',
    name: 'Lumen8 Finals',
    shortName: 'Finals',
    code: 'FIN',
    // TODO: confirm production URL for the Finals Vercel deployment
    url: 'https://finals.lumen8venueflow.com',
    primaryColor: '#22BA91',
    secondaryColor: '#141414',
    status: 'live',
  },
];

// ================= STYLES =================
const styles = {
  container: {
    padding: '40px',
    maxWidth: '1000px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '40px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 700,
    color: THEME.text,
    margin: 0,
    letterSpacing: '-0.02em',
  },
  subtitle: {
    fontSize: '14px',
    color: THEME.textMuted,
    marginTop: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 600,
    background: 'rgba(47, 214, 181, 0.12)',
    color: THEME.accent,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '16px',
  },
  card: {
    background: THEME.surface,
    border: `1px solid ${THEME.border}`,
    borderRadius: '12px',
    padding: '24px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    textDecoration: 'none',
    display: 'block',
    position: 'relative',
    overflow: 'hidden',
  },
  cardHover: {
    background: THEME.surfaceHover,
    border: `1px solid ${THEME.borderHover}`,
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
  },
  cardAccent: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
  },
  clubHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    marginBottom: '16px',
  },
  clubLogo: {
    width: '48px',
    height: '48px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 800,
    fontSize: '16px',
    letterSpacing: '0.05em',
    color: '#fff',
    flexShrink: 0,
  },
  clubName: {
    fontSize: '18px',
    fontWeight: 600,
    color: THEME.text,
    lineHeight: 1.2,
  },
  clubNickname: {
    fontSize: '13px',
    color: THEME.textMuted,
    marginTop: '2px',
  },
  urlRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '4px',
  },
  url: {
    fontSize: '12px',
    color: THEME.accent,
    fontFamily: "'SF Mono', 'Fira Code', monospace",
    opacity: 0.8,
  },
  statusDot: {
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    background: '#22c55e',
    flexShrink: 0,
  },
  arrow: {
    position: 'absolute',
    bottom: '20px',
    right: '20px',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.05)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: THEME.textMuted,
    transition: 'all 0.2s ease',
    fontSize: '14px',
  },
  arrowHover: {
    background: THEME.accentDim,
    color: THEME.accent,
  },
  footer: {
    marginTop: '48px',
    paddingTop: '24px',
    borderTop: `1px solid ${THEME.border}`,
    fontSize: '12px',
    color: THEME.textMuted,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
};

// ================= CLUB CARD =================
function ClubCard({ club }) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <a
      href={club.url}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        ...styles.card,
        ...(isHovered ? styles.cardHover : {}),
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Top accent bar in club primary color */}
      <div
        style={{
          ...styles.cardAccent,
          background: `linear-gradient(90deg, ${club.primaryColor}, ${club.secondaryColor})`,
        }}
      />

      <div style={styles.clubHeader}>
        {/* Club badge */}
        <div
          style={{
            ...styles.clubLogo,
            background: `linear-gradient(135deg, ${club.primaryColor}, ${club.primaryColor}dd)`,
            boxShadow: `0 4px 12px ${club.primaryColor}33`,
          }}
        >
          {club.code}
        </div>

        <div>
          <div style={styles.clubName}>{club.name}</div>
          <div style={styles.clubNickname}>{club.shortName}</div>
        </div>
      </div>

      {/* URL */}
      <div style={styles.urlRow}>
        <div style={styles.statusDot} title="Portal active" />
        <div style={styles.url}>
          {club.url.replace('https://', '')}
        </div>
      </div>

      {/* Arrow indicator */}
      <div
        style={{
          ...styles.arrow,
          ...(isHovered ? styles.arrowHover : {}),
        }}
      >
        ↗
      </div>
    </a>
  );
}

// ================= MAIN COMPONENT =================
export default function PortalHub() {
  const { isProducer, allowedClubs, isOperator } = useAuth();
  
  // Filter portals based on user type
  const livePortals = CLUB_PORTALS.filter(c => {
    // Must be live
    if (c.status !== 'live') return false;
    
    // Operators see all clubs
    if (isOperator) return true;
    
    // Producers with allowed_clubs restriction: filter to their clubs
    if (isProducer && allowedClubs && Array.isArray(allowedClubs) && allowedClubs.length > 0) {
      return allowedClubs.some(allowed => 
        allowed.toUpperCase() === c.id.toUpperCase() || 
        allowed.toUpperCase() === c.code?.toUpperCase()
      );
    }
    
    // Producers without restriction see all (backwards compatible)
    if (isProducer) return true;
    
    // Default: show all
    return true;
  });

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>{isProducer ? 'Your Portals' : 'Portal Hub'}</h1>
        <div style={styles.subtitle}>
          {isProducer ? 'Access your assigned club portals' : 'Quick access to all club VenueFlow portals'}
          <span style={styles.badge}>
            {livePortals.length} {livePortals.length === 1 ? 'portal' : 'portals'}
          </span>
        </div>
      </div>

      {/* Portal Cards Grid */}
      <div style={styles.grid}>
        {livePortals.map(club => (
          <ClubCard key={club.id} club={club} />
        ))}
      </div>

      {/* Footer */}
      <div style={styles.footer}>
        {isProducer 
          ? '🔐 Producer access — Upload content to your assigned clubs'
          : '🔒 Internal — Lumen8 staff only. Each portal requires separate authentication.'
        }
      </div>
    </div>
  );
}
